import { ContentSection } from "~/components/ContentElements/ContentSection";
import { CardSlider } from "~/components/CardSlider";
import { TeaserCard } from "~/components/TeaserCard";
import Location from "@material-design-icons/svg/outlined/location_on.svg?react";
import { CardSliderInset, TeaserCardInset } from "~/components/CardSliderInset";
import { TeaserStacked } from "~/components/TeaserStacked";
import { TeaserHorizontal } from "~/components/TeaserHorizontal";
import { Hero } from "./ContentElements/Hero";
import { CtaWide } from "~/components/CtaWide";
import { LayoutGridTwoCols } from "./Layout/LayoutGridTwoCols";
import { LocationWithImages } from "~/lib/server-functions/get-locations";
import noImageFound from "~/assets/no-image.svg";
import {
  IMAGE_SIZES,
  buildImageUrl,
  resolveAssetUrl,
} from "~/lib/utils";
import { getLocationPath } from "~/lib/utils/location-path";
import { withLocale } from "~/lib/utils/with-locale";
import { MultiStepCompare } from "~/components/MultiStepCompare";
import { FaqSection } from "~/components/FaqSection";
import { Testimonial } from "~/components/Testimonial";
import { CtaWideSimple } from "~/components/CtaWideSimple";
import { LayoutGridThreeCols } from "~/components/Layout/LayoutGridThreeCols";
import { PricingTable } from "~/components/PricingTable";
import CityLinks from "~/components/CityLinks";
import { TeaserHorizontalWide } from "~/components/TeaserHorizontalWide";
import { LogoGrid } from "~/components/LogoGrid";
import { HeadlineText } from "~/components/ContentElements/HeadlineText";
import NewsletterSection from "~/components/NewsletterSection";
import { InfoCard } from "~/components/InfoCard";
import { CtaButtons } from "~/components/CtaButtons";
import { Spacer } from "~/components/Spacer";

type RawFeatureCategory = {
  uid: number;
  name: string;
  features: {
    name: string;
    activeFirstPlan: boolean;
    textFirstPlan?: string;
    activeSecondPlan: boolean;
    textSecondPlan?: string;
    activeThirdPlan: boolean;
    textThirdPlan?: string;
  }[];
};

function transformCategories(raw: RawFeatureCategory[]) {
  return raw.map((cat) => ({
    name: cat.name,
    features: cat.features.map((f) => ({
      name: f.name,
      values: [
        f.textFirstPlan?.length
          ? f.textFirstPlan
          : f.activeFirstPlan
            ? "✔"
            : "✖",
        f.textSecondPlan?.length
          ? f.textSecondPlan
          : f.activeSecondPlan
            ? "✔"
            : "✖",
        f.textThirdPlan?.length
          ? f.textThirdPlan
          : f.activeThirdPlan
            ? "✔"
            : "✖",
      ],
    })),
  }));
}

export function ContentRenderer({ item }: { item: any }) {
  switch (item.type) {
    case "content_section":
      return (
        <ContentSection
          eyebrow={item.data.eyebrow}
          title={item.data.title}
          backgroundColor={item.data.backgroundColor}
        >
          {item.children?.map((child: any) => (
            <ContentRenderer key={child.uid} item={child} />
          ))}
        </ContentSection>
      );

    case "card_slider":
      return (
        <CardSlider
          items={item.data.locations.map((loc: LocationWithImages) => (
            <TeaserCard
              title={loc.name}
              description={loc.descriptionForm?.german || ""}
              image={
                loc.images.length && loc.images[0]?.path
                  ? buildImageUrl(loc.images[0]!.path!, {
                    size: IMAGE_SIZES.cardThumb,
                  })
                  : noImageFound
              }
              link={withLocale(getLocationPath(loc))}
            >
              <p className="text-xl font-semibold flex items-center gap-1 mb-2">
                <Location />
                {loc.city}
              </p>
            </TeaserCard>
          ))}
        />
      );

    case "teaser_stacked":
      return (
        <TeaserStacked
          title={item.data.title}
          description={item.data.description}
          eyebrow={item.data.eyebrow}
          tag={item.data.tag}
          readMoreLabel={item.data.readMoreLabel}
          image={buildImageUrl(item.data.imageIdentifier, {
            size: IMAGE_SIZES.contentImage,
          })}
          link={item.data.link}
        />
      );

    case "teaser_horizontal":
      return (
        <TeaserHorizontal
          eyebrow={item.data.eyebrow}
          title={item.data.title}
          description={item.data.description}
          image={buildImageUrl(item.data.imageIdentifier, {
            size: IMAGE_SIZES.contentImage,
          })}
          link={item.data.link}
          ctaLabel={item.data.ctaLabel}
          inverted={Boolean(item.data.inverted)}
        />
      );

    case "teaser_horizontal_wide":
      return (
        <TeaserHorizontalWide
          eyebrow={item.data.eyebrow}
          title={item.data.title}
          description={item.data.description}
          image={buildImageUrl(item.data.imageIdentifier, {
            size: IMAGE_SIZES.contentImage,
          })}
          link={item.data.link}
          ctaLabel={item.data.ctaLabel}
          ctaLabelColor={item.data.ctaLabelColor}
          secondaryLink={item.data.secondaryLink}
          secondaryCtaLabel={item.data.secondaryCtaLabel}
          inverted={Boolean(item.data.inverted)}
        />
      );

    case "cta_wide":
      if (item.data.innerBox) {
        return (
          <CtaWide
            align={item.data.align}
            title={item.data.title}
            description={item.data.description}
            image={buildImageUrl(item.data.imageIdentifier, {
              size: IMAGE_SIZES.contentImage,
            })}
            link1={item.data.link1}
            link2={item.data.link2}
            ctaLabel1={item.data.ctaLabel1}
            ctaLabel2={item.data.ctaLabel2}
            ctaLabelColor1={item.data.ctaLabelColor1}
          />
        );
      }

      return (
        <CtaWideSimple
          align={item.data.align}
          title={item.data.title}
          description={item.data.description}
          image={buildImageUrl(item.data.imageIdentifier, {
            size: IMAGE_SIZES.contentImage,
          })}
          link1={item.data.link1}
          link2={item.data.link2}
          ctaLabel1={item.data.ctaLabel1}
          ctaLabel2={item.data.ctaLabel2}
          ctaLabelColor1={item.data.ctaLabelColor1}
        />
      );

    case "card_slider_inset":
      return (
        <CardSliderInset
          title={item.data.title}
          description={item.data.description}
          items={item.data.slides.map((slide: any) => (
            <TeaserCardInset
              title={slide.title}
              image={buildImageUrl(slide.imageIdentifier, {
                size: IMAGE_SIZES.cardThumb,
              })}
              link={
                slide.link
                  ? slide.link.startsWith("http") ||
                    slide.link.startsWith("mailto:") ||
                    slide.link.startsWith("tel:")
                    ? slide.link
                    : withLocale(slide.link)
                  : withLocale("/")
              }
            />
          ))}
        />
      );

    case "locationguide_hero":
      return (
        <Hero
          header={item.data.header}
          aston={item.data.aston}
          astonButtonLabel={item.data.buttonLabel}
          locations={item.data.locations}
          tagline={item.data.tagline || ""}
        />
      );

    case "container-single":
      return (
        <ContentSection
          backgroundColor={item.data.backgroundColor}
          eyebrow={item.data.container_eyebrow}
          titleAlign={item.data.titleAlign}
          description={item.data.container_description}
          title={item.data.header}
          link={
            item.data.container_link
              ? item.data.container_link.startsWith("http") ||
                item.data.container_link.startsWith("mailto:") ||
                item.data.container_link.startsWith("tel:")
                ? item.data.container_link
                : withLocale(item.data.container_link)
              : item.data.container_link
          }
          linkLabel={item.data.container_link_label}
        >
          {item.data.children?.map((child: any) => (
            <ContentRenderer key={child.uid} item={child} />
          ))}
        </ContentSection>
      );

    case "container-two-columns":
      return (
        <ContentSection
          backgroundColor={item.data.backgroundColor}
          eyebrow={item.data.container_eyebrow}
          titleAlign={item.data.titleAlign}
          description={item.data.container_description}
          title={item.data.header}
          link={
            item.data.container_link
              ? item.data.container_link.startsWith("http") ||
                item.data.container_link.startsWith("mailto:") ||
                item.data.container_link.startsWith("tel:")
                ? item.data.container_link
                : withLocale(item.data.container_link)
              : item.data.container_link
          }
          linkLabel={item.data.container_link_label}
        >
          <LayoutGridTwoCols>
            <div>
              {item.data.children
                ?.filter((i: any) => i.colPos === 201)
                ?.map((child: any) => (
                  <ContentRenderer key={child.uid} item={child} />
                ))}
            </div>
            <div>
              {item.data.children
                ?.filter((i: any) => i.colPos === 202)
                ?.map((child: any) => (
                  <ContentRenderer key={child.uid} item={child} />
                ))}
            </div>
          </LayoutGridTwoCols>
        </ContentSection>
      );

    case "container-three-columns":
      return (
        <ContentSection
          backgroundColor={item.data.backgroundColor}
          eyebrow={item.data.container_eyebrow}
          description={item.data.container_description}
          title={item.data.header}
          titleAlign={item.data.titleAlign}
          link={item.data.container_link}
          linkLabel={item.data.container_link_label}
        >
          <LayoutGridThreeCols>
            <div>
              {item.data.children
                ?.filter((i: any) => i.colPos === 201)
                ?.map((child: any) => (
                  <ContentRenderer key={child.uid} item={child} />
                ))}
            </div>
            <div>
              {item.data.children
                ?.filter((i: any) => i.colPos === 202)
                ?.map((child: any) => (
                  <ContentRenderer key={child.uid} item={child} />
                ))}
            </div>
            <div>
              {item.data.children
                ?.filter((i: any) => i.colPos === 203)
                ?.map((child: any) => (
                  <ContentRenderer key={child.uid} item={child} />
                ))}
            </div>
          </LayoutGridThreeCols>
        </ContentSection>
      );

    case "multi_step_compare":
      return (
        <MultiStepCompare
          steps={item.data.steps.map((step: any, index: number) => ({
            id: String(index + 1),
            title: step.title,
            description: step.description,
            icon: step.icon,
            video: step.videoIdentifier ? `${import.meta.env.VITE_ASSET_URL}${step.videoIdentifier}` : undefined,
            videoPoster: buildImageUrl(step.videoPosterIdentifier, {
              size: IMAGE_SIZES.hero,
            }),
            videoPosterCrop: step.videoPosterCrop,
          }))}
        />
      );

    case "faq":
      return (
        <FaqSection
          title={item.data.title}
          description={item.data.description}
          showButton={item.data.showButton}
          buttonLabel={item.data.buttonLabel}
          buttonLink={item.data.buttonLink}
          items={item.data.items}
        />
      );

    case "testimonial":
      return (
        <Testimonial
          stars={item.data.stars}
          align={item.data.align}
          orientation={item.data.orientation}
          quote={item.data.text}
          name={item.data.authorName}
          role={item.data.authorRole}
          avatar={buildImageUrl(item.data.authorImageIdentifier, {
            size: IMAGE_SIZES.avatar,
          })}
          companyLogo={buildImageUrl(item.data.companyLogoIdentifier, {
            size: IMAGE_SIZES.logo,
          })}
        />
      );
    case "pricing_table":
      return (
        <PricingTable
          plans={item.data.plans}
          featureCategories={transformCategories(item.data.featureCategories)}
        />
      );

    case "city_links":
      return (
        <CityLinks cities={item.data.map((c: { name: string }) => c.name)} />
      );

    case "logo_grid":
      return (
        <LogoGrid
          logos={item.data.map((l: { logoIdentifier: string; alt: string }) => {
            return {
              src: buildImageUrl(l.logoIdentifier, {
                size: IMAGE_SIZES.logo,
              }),
              alt: l.alt,
            };
          })}
        />
      );

    case "headline_text":
      return (
        <HeadlineText headline={item.data.headline} text={item.data.text} />
      );

    case "newsletter":
      return <NewsletterSection {...item.data} />;

    case "info_card":
      return (
        <InfoCard
          link={item.data.link}
          linkLabel={item.data.linkLabel}
          icon={
            item.data.iconIdentifier
              ? `${import.meta.env.VITE_ASSET_URL}${item.data.iconIdentifier}`
              : null
          }
          headline={item.data.headline}
          text={item.data.text}
          align={item.data.align}
        />
      );

    case "cta_buttons":
      return (
        <CtaButtons
          align={item.data.align}
          link={item.data.link}
          ctaLabel={item.data.ctaLabel}
          ctaLabelColor={item.data.ctaLabelColor}
          secondaryLink={item.data.secondaryLink}
          secondaryCtaLabel={item.data.secondaryCtaLabel}
        />
      );

    case "spacer":
      return <Spacer height={item.data.height} />;

    default:
      return (
        <div style={{ color: "red" }}>Unknown content type: {item.type}</div>
      );
  }
}
